'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  Crown,
  Sparkles,
  MapPin,
  Plus,
  Check,
  ShoppingBag,
  Scroll,
  Hand,
  Quote,
  Package,
  Globe2,
  Compass,
  Clock,
  Navigation,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArticleCard } from '@/components/site/article-card'
import { ProvenanceMap } from '@/components/site/provenance-map'
import { useCart } from '@/lib/cart-store'
import type { Article, Rayon } from '@/lib/site-config'

type Tab = 'essence' | 'savoirFaire' | 'rituel' | 'provenance'

// Coordonnées approximatives Paris → capitale de chaque rayon
const RAYON_COORDS: Record<string, { city: string; country: string; lat: number; lng: number; kmFromParis: number; daysToParis: string }> = {
  'tresor-mediterranee': { city: 'Fès', country: 'Maroc', lat: 34.03, lng: -5.0, kmFromParis: 1850, daysToParis: '7 jours par voie maritime' },
  'jakarta': { city: 'Jakarta', country: 'Indonésie', lat: -6.21, lng: 106.85, kmFromParis: 11500, daysToParis: '21 jours par voie aérienne' },
  'rohoullah-khameini': { city: 'Ispahan', country: 'Iran', lat: 32.65, lng: 51.67, kmFromParis: 4100, daysToParis: '10 jours par voie terrestre' },
  'ndiamsyl-mody': { city: 'Touba', country: 'Sénégal', lat: 14.85, lng: -15.88, kmFromParis: 4200, daysToParis: '12 jours par voie maritime' },
  'ivoire-tripoli': { city: 'Tripoli', country: 'Liban', lat: 34.43, lng: 35.85, kmFromParis: 3200, daysToParis: '8 jours par voie aérienne' },
  'medinatoul-rassulullah': { city: 'Médine', country: 'Arabie Saoudite', lat: 24.47, lng: 39.61, kmFromParis: 4500, daysToParis: '9 jours par voie aérienne' },
  'tanzania-ocean': { city: 'Zanzibar', country: 'Tanzanie', lat: -6.13, lng: 39.32, kmFromParis: 7100, daysToParis: '15 jours par voie maritime' },
  'afro-black': { city: 'Kumasi', country: 'Ghana', lat: 6.69, lng: -1.62, kmFromParis: 5000, daysToParis: '14 jours par voie maritime' },
  'kuarlumpur': { city: 'Kuala Lumpur', country: 'Malaisie', lat: 3.14, lng: 101.69, kmFromParis: 10500, daysToParis: '18 jours par voie aérienne' },
}

export function ArticleDetailClient({
  article,
  rayon,
  isPrivilege,
  suggestions,
  prevRayon,
  nextRayon,
}: {
  article: Article
  rayon: Rayon
  isPrivilege: boolean
  suggestions: Article[]
  prevRayon: Rayon | null
  nextRayon: Rayon | null
}) {
  const addItem = useCart(s => s.addItem)
  const openCart = useCart(s => s.openCart)
  const [added, setAdded] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('essence')
  const [quantity, setQuantity] = useState(1)
  const [activeImageIdx, setActiveImageIdx] = useState(0)

  const imageUrl = `/images/products/${article.rayon}/${article.rayon}-${String(article.imageIndex + 1).padStart(3, '0')}.png`

  // Construire 4 thumbnails à partir d'images réelles du même rayon
  // On prend l'image principale + 3 autres imageIndex différents
  const galleryImages = useMemo(() => {
    const total = 52 // chaque rayon a ~52 images
    const primary = article.imageIndex
    // Sélectionner 3 indices différents, espacés
    const others = [
      (primary + 13) % total,
      (primary + 26) % total,
      (primary + 39) % total,
    ]
    const indices = [primary, ...others]
    return indices.map((idx, i) => ({
      idx,
      src: `/images/products/${article.rayon}/${article.rayon}-${String(idx + 1).padStart(3, '0')}.png`,
      alt: `${article.name} — vue ${i + 1}`,
      isPrimary: i === 0,
    }))
  }, [article])

  // Image principale affichée (suit la thumbnail sélectionnée)
  const mainImage = galleryImages[activeImageIdx] ?? galleryImages[0]

  const handleAdd = () => {
    addItem(article, quantity)
    setAdded(true)
    setTimeout(() => {
      setAdded(false)
      openCart()
    }, 800)
  }

  // Coordonnées de provenance
  const provenance = RAYON_COORDS[article.rayon]

  // Onglets disponibles
  const tabs: { id: Tab; label: string; icon: typeof Sparkles; available: boolean }[] = [
    { id: 'essence', label: 'Essence', icon: Sparkles, available: Boolean(article.essence) },
    { id: 'savoirFaire', label: 'Savoir-faire', icon: Hand, available: Boolean(article.savoirFaire) },
    { id: 'rituel', label: 'Rituel', icon: Scroll, available: Boolean(article.rituel) },
    { id: 'provenance', label: 'Provenance', icon: Globe2, available: Boolean(provenance) },
  ].filter(t => t.available) as { id: Tab; label: string; icon: typeof Sparkles; available: boolean }[]

  const activeTabObj = tabs.find(t => t.id === activeTab) ?? tabs[0]

  const activeContent = activeTab === 'essence'
    ? article.essence
    : activeTab === 'savoirFaire'
    ? article.savoirFaire
    : activeTab === 'rituel'
    ? article.rituel
    : undefined

  return (
    <>
      {/* Breadcrumb */}
      <div className="border-b border-gold/20 bg-secondary/20">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-xs text-foreground/60">
            <Link href="/#rayons" className="hover:text-gold transition-colors">Rayons</Link>
            <span className="text-gold/40">/</span>
            <Link href={`/#${rayon.slug}`} className="hover:text-gold transition-colors flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: rayon.accentColor }} />
              {rayon.name}
            </Link>
            <span className="text-gold/40">/</span>
            <span className="text-cuir font-medium truncate">{article.name}</span>
          </nav>
        </div>
      </div>

      {/* HERO — Article detail */}
      <section className="py-10 md:py-16 relative overflow-hidden">
        {/* Ornamental background */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(circle_at_20%_30%,theme(colors.gold.DEFAULT)_0,transparent_50%)]" />

        <div className="container mx-auto px-4 relative z-10">
          <Link
            href={`/#${rayon.slug}`}
            className="inline-flex items-center gap-2 text-sm text-gold hover:text-amber-200 transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour au rayon {rayon.name}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* LEFT — Image gallery */}
            <div>
              <motion.div
                key={mainImage.src}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className={`relative aspect-square overflow-hidden rounded-2xl border ${
                  isPrivilege
                    ? 'border-gold/60 shadow-luxe-deep'
                    : 'border-gold/20 shadow-luxury'
                }`}
              >
                <Image
                  src={mainImage.src}
                  alt={article.name}
                  fill
                  priority
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cuir/40 via-transparent to-transparent pointer-events-none" />

                {/* Privilège ribbon */}
                {isPrivilege && (
                  <div className="absolute top-0 left-0 z-20">
                    <div className="gradient-gold px-4 py-2 rounded-br-2xl flex items-center gap-2 shadow-luxury">
                      <Crown className="h-4 w-4 text-cuir" />
                      <span className="text-xs font-bold uppercase tracking-wider text-cuir">
                        Privilège
                      </span>
                    </div>
                  </div>
                )}

                {/* Badge */}
                {article.badge && (
                  <div className={`absolute top-4 ${isPrivilege ? 'left-32' : 'left-4'}`}>
                    <Badge
                      variant="outline"
                      className="text-[10px] uppercase tracking-wider bg-cuir/85 text-gold border border-gold/40 backdrop-blur"
                    >
                      {article.badge}
                    </Badge>
                  </div>
                )}

                {/* Eva Diaw signature */}
                <div className="absolute bottom-4 right-4">
                  <Badge
                    variant="secondary"
                    className="text-[9px] uppercase tracking-wider bg-cuir/85 text-gold border border-gold/40 backdrop-blur"
                  >
                    Eva Diaw · Sélection
                  </Badge>
                </div>

                {/* Vue numéro indicator */}
                <div className="absolute bottom-4 left-4">
                  <Badge
                    variant="secondary"
                    className="text-[9px] uppercase tracking-wider bg-cuir/70 text-primary-foreground/80 border border-gold/30 backdrop-blur"
                  >
                    Vue {activeImageIdx + 1} / 4
                  </Badge>
                </div>
              </motion.div>

              {/* Thumbnail strip — vraies images variées du même rayon */}
              <div className="mt-4 grid grid-cols-4 gap-3">
                {galleryImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImageIdx(i)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      i === activeImageIdx
                        ? 'border-gold shadow-gold'
                        : 'border-gold/20 hover:border-gold/60'
                    }`}
                    aria-label={`Voir la vue ${i + 1}`}
                  >
                    <Image
                      src={img.src}
                      alt={img.alt}
                      fill
                      sizes="15vw"
                      className="object-cover"
                    />
                    {i === 0 && (
                      <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider bg-cuir/85 text-gold border border-gold/40">
                        Principale
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Note sur les vues */}
              <p className="mt-3 text-[10px] text-foreground/50 italic text-center">
                Cliquez sur une vue pour l'agrandir · Photographies Eva Diaw
              </p>
            </div>

            {/* RIGHT — Article info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col"
            >
              {/* Rayon chip */}
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: rayon.accentColor }}
                />
                <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-medium">
                  {rayon.name} · {rayon.region}
                </span>
              </div>

              {/* Title */}
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-cuir leading-tight mb-3">
                {article.name}
              </h1>

              {/* Origin */}
              <div className="flex items-center gap-2 text-sm text-foreground/70 mb-4">
                <MapPin className="h-4 w-4 text-gold" />
                <span className="italic">{article.origin}</span>
                <span className="text-gold/40">·</span>
                <span>{article.category}</span>
              </div>

              {/* Description */}
              <p className="text-base text-foreground/80 leading-relaxed mb-6">
                {article.description}
              </p>

              {/* Essence citation (pièces Privilège) */}
              {isPrivilege && article.essence && (
                <div className="relative mb-6 p-5 rounded-xl gradient-cuir text-primary-foreground overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 gradient-gold" />
                  <div className="absolute bottom-0 left-0 right-0 h-1 gradient-gold opacity-50" />
                  <Quote className="h-6 w-6 text-gold mb-2 opacity-80" />
                  <p className="font-display text-base md:text-lg italic text-primary-foreground/90 leading-relaxed">
                    « {article.essence} »
                  </p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-gold">
                    — Essence, par Eva Diaw
                  </p>
                </div>
              )}

              {/* Price + cart */}
              <div className="p-6 rounded-xl border border-gold/30 bg-card shadow-luxury mb-6">
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50 mb-1">
                      Prix
                    </div>
                    <div className="font-display text-4xl md:text-5xl font-bold text-gradient-gold">
                      {article.price.toFixed(2).replace('.', ',')} €
                      {article.unit && (
                        <span className="ml-2 text-base font-normal text-foreground/60">
                          {article.unit}
                        </span>
                      )}
                    </div>
                    {isPrivilege && (
                      <p className="mt-1 text-[10px] text-gold/80 italic">
                        Carte d'authenticité signée incluse
                      </p>
                    )}
                  </div>
                  {isPrivilege && (
                    <Crown className="h-12 w-12 text-gold/60" />
                  )}
                </div>

                {/* Quantity selector */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs uppercase tracking-wider text-foreground/60">
                    Quantité
                  </span>
                  <div className="flex items-center border border-gold/40 rounded-full overflow-hidden">
                    <button
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="h-9 w-9 flex items-center justify-center text-cuir hover:bg-gold/10 transition-colors text-lg"
                      aria-label="Diminuer"
                    >
                      −
                    </button>
                    <span className="w-10 text-center font-display font-semibold text-cuir">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(q => q + 1)}
                      className="h-9 w-9 flex items-center justify-center text-cuir hover:bg-gold/10 transition-colors text-lg"
                      aria-label="Augmenter"
                    >
                      +
                    </button>
                  </div>
                </div>

                <Button
                  onClick={handleAdd}
                  size="lg"
                  className={`w-full h-12 rounded-full font-medium transition-all ${
                    added
                      ? 'gradient-emerald-gold'
                      : 'gradient-gold text-cuir hover:opacity-90 shadow-luxury'
                  }`}
                >
                  {added ? (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Ajouté au panier
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="h-5 w-5 mr-2" />
                      Ajouter au panier — {(article.price * quantity).toFixed(2).replace('.', ',')} €
                    </>
                  )}
                </Button>
              </div>

              {/* Quick facts */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-3 rounded-lg border border-gold/20 bg-secondary/20 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-foreground/50 mb-1">
                    Rayon
                  </div>
                  <div className="text-xs font-semibold text-cuir truncate">
                    {rayon.name}
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-gold/20 bg-secondary/20 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-foreground/50 mb-1">
                    Origine
                  </div>
                  <div className="text-xs font-semibold text-cuir truncate">
                    {article.origin.split(',')[0]}
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-gold/20 bg-secondary/20 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-foreground/50 mb-1">
                    Catégorie
                  </div>
                  <div className="text-xs font-semibold text-cuir truncate">
                    {article.category}
                  </div>
                </div>
              </div>

              {/* Privilège note */}
              {isPrivilege && (
                <div className="p-4 rounded-xl border border-gold/40 bg-gold/5">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-display text-sm font-semibold text-cuir mb-1">
                        Pièce Privilège — Sélection Eva Diaw
                      </h3>
                      <p className="text-xs text-foreground/70 leading-relaxed">
                        Cette pièce est accompagnée de sa carte d'authenticité signée,
                        numérotée et datée. Elle est éligible aux services signature de la
                        Maison Eva Diaw : emballage doré à chaud, livraison en gant blanc
                        à Paris, et curation privée sur rendez-vous.
                      </p>
                      <Link
                        href="/services-luxe"
                        className="inline-flex items-center gap-1 mt-2 text-xs text-gold hover:underline"
                      >
                        Voir les 6 services signature
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* TABS — essence / savoir-faire / rituel / provenance */}
      {tabs.length > 0 && activeTabObj && (
        <section className="py-12 md:py-16 bg-secondary/30 border-y border-gold/20">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-gold mb-2">
                {isPrivilege ? 'Pièce Privilège · Signature Eva Diaw' : 'Le récit d\'une pièce'}
              </p>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-cuir">
                L'<span className="text-gradient-gold">âme</span>, le geste, le rituel, la provenance
              </h2>
              <p className="text-sm text-foreground/60 mt-3 max-w-2xl mx-auto">
                {isPrivilege
                  ? 'Quatre versances poétiques et artisanales signées de la main d\'Eva Diaw pour révéler la vérité de cette pièce.'
                  : 'Découvrez l\'origine, le voyage et le récit de cette pièce sélectionnée par Eva Diaw.'}
              </p>
            </div>

            {/* Tabs nav */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {tabs.map(tab => {
                const Icon = tab.icon
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 border ${
                      isActive
                        ? 'gradient-gold text-cuir border-gold shadow-luxury'
                        : 'bg-card text-cuir border-gold/30 hover:border-gold/60'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                )
              })}
            </div>

            {/* Active content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="relative p-8 md:p-10 rounded-2xl bg-card border border-gold/30 shadow-luxury overflow-hidden"
            >
              {/* Decorative corners */}
              <div className="absolute top-3 left-3 h-6 w-6 border-t border-l border-gold/40 pointer-events-none" />
              <div className="absolute top-3 right-3 h-6 w-6 border-t border-r border-gold/40 pointer-events-none" />
              <div className="absolute bottom-3 left-3 h-6 w-6 border-b border-l border-gold/40 pointer-events-none" />
              <div className="absolute bottom-3 right-3 h-6 w-6 border-b border-r border-gold/40 pointer-events-none" />

              <div className="flex items-start gap-4 mb-4">
                {(() => {
                  const Icon = activeTabObj.icon
                  return (
                    <div className="h-12 w-12 rounded-full gradient-gold flex items-center justify-center flex-shrink-0 shadow-luxury">
                      <Icon className="h-6 w-6 text-cuir" />
                    </div>
                  )
                })()}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-1">
                    {activeTabObj?.label}
                  </p>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-cuir">
                    {activeTabObj?.id === 'essence' && "L'âme véritable de cette pièce"}
                    {activeTabObj?.id === 'savoirFaire' && "Le geste qui l'a fait naître"}
                    {activeTabObj?.id === 'rituel' && 'Comment la vivre, comment la garder'}
                    {activeTabObj?.id === 'provenance' && provenance && `Depuis ${provenance.city}, ${provenance.country}`}
                  </h3>
                </div>
              </div>

              {activeTabObj?.id === 'provenance' && provenance ? (
                <div className="pl-16">
                  <ProvenanceMap
                    lat={provenance.lat}
                    lng={provenance.lng}
                    city={provenance.city}
                    country={provenance.country}
                    rayonName={rayon.name}
                    origin={article.origin}
                  />

                  {/* Stats de provenance */}
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg border border-gold/20 bg-secondary/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Navigation className="h-3.5 w-3.5 text-gold" />
                        <span className="text-[9px] uppercase tracking-wider text-foreground/50">
                          Distance de Paris
                        </span>
                      </div>
                      <div className="font-display text-base font-semibold text-cuir">
                        {provenance.kmFromParis.toLocaleString('fr-FR')} km
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border border-gold/20 bg-secondary/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-3.5 w-3.5 text-gold" />
                        <span className="text-[9px] uppercase tracking-wider text-foreground/50">
                          Acheminement
                        </span>
                      </div>
                      <div className="font-display text-xs font-semibold text-cuir">
                        {provenance.daysToParis}
                      </div>
                    </div>
                    <div className="p-3 rounded-lg border border-gold/20 bg-secondary/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Compass className="h-3.5 w-3.5 text-gold" />
                        <span className="text-[9px] uppercase tracking-wider text-foreground/50">
                          Coordonnées
                        </span>
                      </div>
                      <div className="font-display text-xs font-semibold text-cuir">
                        {provenance.lat.toFixed(2)}° {provenance.lat >= 0 ? 'N' : 'S'}
                        {' · '}
                        {provenance.lng.toFixed(2)}° {provenance.lng >= 0 ? 'E' : 'O'}
                      </div>
                    </div>
                  </div>

                  <p className="mt-5 text-sm text-foreground/70 leading-relaxed italic">
                    « Chaque pièce voyage depuis son atelier d'origine jusqu'au boulevard
                    de la Villette. Sur sa route, elle porte la mémoire de l'artisan, le
                    climat de son territoire, et le temps patient de son acheminement. La
                    Maison Eva Diaw consigne ce voyage dans le dossier de provenance
                    accompagnant chaque pièce Privilège. »
                  </p>

                  {!isPrivilege && (
                    <Link
                      href="/services-luxe"
                      className="inline-flex items-center gap-1.5 mt-4 text-xs text-gold hover:underline"
                    >
                      <Sparkles className="h-3 w-3" />
                      Découvrir le service « Atelier Provenance »
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              ) : (
                <p className="font-display text-base md:text-lg italic text-foreground/80 leading-relaxed pl-16">
                  {activeContent}
                </p>
              )}

              {activeTabObj?.id !== 'provenance' && (
                <p className="mt-6 text-xs text-right text-foreground/50 italic">
                  — Eva Diaw, curatrice de la Maison
                </p>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* SUGGESTIONS — Articles du même rayon */}
      {suggestions.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">
                Dans le même rayon
              </p>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-cuir">
                À <span className="text-gradient-gold">découvrir</span> également
              </h2>
              <p className="text-sm text-foreground/60 mt-2">
                D'autres pièces du rayon {rayon.name}, sélectionnées par Eva Diaw.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {suggestions.map(a => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                href={`/#${rayon.slug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gold/40 text-cuir hover:bg-gold/10 transition-colors font-medium"
              >
                <Package className="h-4 w-4 text-gold" />
                Voir les 63 articles du rayon {rayon.name}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA — Services Luxe (pièces Privilège uniquement) */}
      {isPrivilege && (
        <section className="py-16 md:py-20 gradient-cuir text-primary-foreground">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <Crown className="h-12 w-12 text-gold mx-auto mb-4" />
            <h2 className="font-display text-2xl md:text-4xl font-bold mb-4">
              Sublimer cette pièce Privilège
            </h2>
            <p className="text-base text-primary-foreground/80 leading-relaxed mb-8">
              Pour honorer cette pièce d'exception, la Maison Eva Diaw a composé un
              cérémonial de six services signature : emballage doré à chaud, livraison en
              gant blanc à Paris, carte d'authenticité, curation privée, personnalisation
              et atelier de provenance.
            </p>
            <Link
              href="/services-luxe"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full gradient-gold text-cuir font-medium hover:opacity-90 transition-opacity shadow-luxury"
            >
              <Sparkles className="h-4 w-4" />
              Découvrir les 6 services signature
            </Link>
          </div>
        </section>
      )}

      {/* NAVIGATION LATÉRALE — Rayon précédent → Rayon suivant */}
      {prevRayon && nextRayon && (
        <section className="py-12 md:py-16 border-t border-gold/20 bg-secondary/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">
                Voyager entre les rayons
              </p>
              <h2 className="font-display text-xl md:text-2xl font-bold text-cuir">
                Le <span className="text-gradient-gold">tour du monde</span> continue
              </h2>
              <p className="text-xs text-foreground/60 mt-2">
                Neuf rayons, neuf territoires — passez du rayon actuel à ses voisins.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
              {/* Rayon précédent */}
              <Link
                href={`/#${prevRayon.slug}`}
                className="group relative p-5 md:p-6 rounded-xl bg-card border border-gold/20 hover:border-gold/60 hover:shadow-luxury transition-all flex items-center gap-4 overflow-hidden"
              >
                <div className="absolute -top-8 -left-8 h-24 w-24 rounded-full bg-gold/[0.04] group-hover:bg-gold/[0.08] transition-colors pointer-events-none" />
                <div className="flex-shrink-0 h-12 w-12 rounded-full border-2 flex items-center justify-center transition-transform group-hover:-translate-x-1"
                  style={{ borderColor: prevRayon.accentColor }}
                >
                  <ArrowLeft className="h-5 w-5 text-cuir" />
                </div>
                <div className="flex-1 min-w-0 relative z-10">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-foreground/50 mb-1">
                    Rayon précédent
                  </p>
                  <h3 className="font-display text-base md:text-lg font-semibold text-cuir leading-tight group-hover:text-gold transition-colors">
                    {prevRayon.name}
                  </h3>
                  <p className="text-[11px] text-foreground/60 italic truncate">
                    {prevRayon.subtitle}
                  </p>
                </div>
                <div
                  className="absolute bottom-0 left-0 h-0.5 transition-all duration-500 group-hover:w-full w-0"
                  style={{ backgroundColor: prevRayon.accentColor }}
                />
              </Link>

              {/* Rayon suivant */}
              <Link
                href={`/#${nextRayon.slug}`}
                className="group relative p-5 md:p-6 rounded-xl bg-card border border-gold/20 hover:border-gold/60 hover:shadow-luxury transition-all flex items-center gap-4 overflow-hidden"
              >
                <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gold/[0.04] group-hover:bg-gold/[0.08] transition-colors pointer-events-none" />
                <div className="flex-1 min-w-0 relative z-10 text-right">
                  <p className="text-[9px] uppercase tracking-[0.25em] text-foreground/50 mb-1">
                    Rayon suivant
                  </p>
                  <h3 className="font-display text-base md:text-lg font-semibold text-cuir leading-tight group-hover:text-gold transition-colors">
                    {nextRayon.name}
                  </h3>
                  <p className="text-[11px] text-foreground/60 italic truncate">
                    {nextRayon.subtitle}
                  </p>
                </div>
                <div className="flex-shrink-0 h-12 w-12 rounded-full border-2 flex items-center justify-center transition-transform group-hover:translate-x-1"
                  style={{ borderColor: nextRayon.accentColor }}
                >
                  <ArrowRight className="h-5 w-5 text-cuir" />
                </div>
                <div
                  className="absolute bottom-0 right-0 h-0.5 transition-all duration-500 group-hover:w-full w-0"
                  style={{ backgroundColor: nextRayon.accentColor }}
                />
              </Link>
            </div>

            {/* Retour à la carte des rayons */}
            <div className="text-center mt-8">
              <Link
                href="/#rayons"
                className="inline-flex items-center gap-2 text-xs text-gold hover:underline"
              >
                <Compass className="h-3.5 w-3.5" />
                Revoir les 9 rayons du monde
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  )
}
